import { ErrorHandler, Injectable } from '@angular/core';
import { trace, SpanStatusCode, context as otelContext } from '@opentelemetry/api';


@Injectable({
  providedIn: 'root'
})
export class TelemetryErrorHandler implements ErrorHandler {

  protected readonly tracer = trace.getTracer('angular-error-handler');

  public handleError(error: Error): void{
    // Check for active span and use its context
    const activeSpan = trace.getSpan(otelContext.active());

    let span;
    if (activeSpan) {
      // Create error span in the same trace as the active span
      span = this.tracer.startSpan(
        'error.angular.uncaught', 
        {
          attributes: {
            'parent.span_id': activeSpan.spanContext().spanId,
            'parent.trace_id': activeSpan.spanContext().traceId,
            
            'error.stack': error.stack || '',
            'error.type': error.name || 'Unknown',
            'error.message': error.message || 'Unknown error',
            
            'error.timestamp': Date.now().toString(),
            'error.component': this.extractComponentName(error)
          }
        }, 
        trace.setSpan(otelContext.active(), activeSpan)
      );
    } 
    else {
      // No active span, create standalone error span
      span = this.tracer.startSpan(
        'error.angular.uncaught', 
        {
          attributes: {
            'error.stack': error.stack || '',
            'error.type': error.name || 'Unknown',
            'error.message': error.message || 'Unknown error',
            
            'error.timestamp': Date.now().toString(),
            'error.component': this.extractComponentName(error)
          }
        }
      );
    }

    // Record the exception
    span.recordException(error);

    // Set error status
    span.setStatus(
      { 
        code: SpanStatusCode.ERROR, 
        message: `Angular Error Handler caught: ${error.message}` 
      }
    );

    // Add error lifecycle event
    span.addEvent(
      'error.caught', 
      {
        'error.name': error.name || 'Unknown',
        'error.message': error.message || 'Unknown error',
        'error.timestamp': Date.now().toString()
      }
    );

    // End the span
    span.end();

    // Log to console with additional context
    console.error(
      'Error caught by Angular Error Handler:', 
      {
        error,
        spanId: span.spanContext().spanId,
        traceId: span.spanContext().traceId,
        component: this.extractComponentName(error)
      }
    );
    // Call original error handling
    throw error;
  }

  protected extractComponentName(error: Error): string{
    // Try to extract component name from error stack
    if (error.stack) {
      // Match component method patterns, Component.ngOnInit, etc.
      const componentMatch = error.stack.match(/at (_?\w+)\.(\w+)/);
      
      if (componentMatch) {
        const methodName = componentMatch[2];
        const componentName = componentMatch[1];
        
        // Return component name with method for better context
        return `${ componentName }.${ methodName }`;
      }
      
      // Fallback to lifecycle methods if no component method found
      const lifecycleMatch = error.stack.match(/at (\w+)\.ngOnChanges|at (\w+)\.ngOnInit|at (\w+)\.ngAfterViewInit/);
      
      if (lifecycleMatch) {
        return lifecycleMatch[1] || lifecycleMatch[2] || lifecycleMatch[3] || 'Unknown';
      }
    }
    return 'Unknown';
  }
}
